import { Dialog, DialogProps, Stack, Typography } from '@mui/material';
import { ComponentType, FC, PropsWithChildren } from 'react';
import Icon from '@/components/ui/Icon';
import { IconType } from '@/assets/icons';
import style from './style';
import IconButton from '@/components/ui/IconButton';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: IconType;
  ActionArea?: ComponentType;
  /**
   * DS-Dialog-Standard (DESIGN_SYSTEM Teil B, Runde 3b): Breitensteuerung.
   * Additiv — Defaults entsprechen exakt dem Bestandsverhalten (MUI-Default
   * maxWidth 'sm', kein fullWidth), bestehende Aufrufer unverändert.
   */
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  /**
   * Bleibt offen, bis der Inhalt ihn schliesst: kein Schliessen-X, kein ESC,
   * kein Backdrop-Klick. Additiv (Default false = Bestandsverhalten).
   * Abweichung vom DS-Standard „X, ESC und Backdrop schliessen" — nur per
   * dokumentiertem Task-Doc-Entscheid, heute genau einer: das Admin-Popup
   * fuer kritische System-Meldungen (Decision #207, Alex 16.09.2026,
   * docs/tasks/phase-0/admin-notifications-popup.md).
   */
  persistent?: boolean;
  /**
   * Haelt die Seite bedienbar: kein Abdunkeln, kein Fokus-Fang, Klicks
   * neben dem Fenster erreichen die Seite; das Fenster sitzt unten rechts.
   * Additiv (Default false = Bestandsverhalten). Heute genau ein Aufrufer:
   * das Popup „Mandant wartet" (Paket 1, docs/tasks/belieferung-doppelbelegung.md
   * §5N.2) — sein Knopf „Ändern" springt in den Kalender, und der muss sich
   * dort ziehen lassen, waehrend das Popup offen bleibt.
   */
  nichtBlockierend?: boolean;
}

const Modal: FC<PropsWithChildren<ModalProps>> = ({
  open,
  onClose,
  icon,
  subtitle,
  title,
  children,
  ActionArea,
  maxWidth,
  fullWidth,
  persistent = false,
  nichtBlockierend = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={persistent ? undefined : onClose}
      disableEscapeKeyDown={persistent}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: nichtBlockierend
          ? { pointerEvents: 'auto', position: 'fixed', right: 24, bottom: 24, m: 0 }
          : undefined,
      }}
      {...(nichtBlockierend
        ? {
            hideBackdrop: true,
            disableEnforceFocus: true,
            disableAutoFocus: true,
            disableRestoreFocus: true,
            disableScrollLock: true,
            sx: { pointerEvents: 'none' },
          }
        : {})}
    >
      <Stack sx={style.container}>
        {/* Ohne Symbol und ohne Schliessen-X bleibt die Zeile leer — dann gar
            nicht rendern (Sichttest Alex 08.10.2026, Popup „Mandant wartet"). */}
        {(!!icon || !persistent) && (
          <Stack sx={style.iconGroup}>
            {!!icon && <Icon icon={icon} />}
            {!persistent && <IconButton icon="Close" onClick={onClose} />}
          </Stack>
        )}
        <Stack sx={style.kopf}>
          <Typography variant="seitentitel" component="h2">{title}</Typography>
          {!!subtitle && <Typography variant="meta">{subtitle}</Typography>}
        </Stack>
        <Stack>{children}</Stack>
      </Stack>
      {!!ActionArea && <ActionArea />}
    </Dialog>
  );
};

export default Modal;
